import React, { useEffect, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  SelectElement,
  TextFieldElement,
  TextareaAutosizeElement,
  set,
  useForm,
} from "react-hook-form-mui";
import { map, z } from "zod";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  anthropic_EndpointTemplate,
  anthropic_bodyTemplate,
  anthropic_headerTemplate,
  anthropic_inputPricePerMillion,
  anthropic_outputPricePerMillion,
  azure_EndpointTemplate,
  azure_bodyTemplate,
  azure_headerTemplate,
  azure_inputPricePerMillion,
  azure_outputPricePerMillion,
  bedrock_EndpointTemplate,
  bedrock_bodyTemplate,
  bedrock_headerTemplate,
  chaos_EndpointTemplate,
  chaos_bodyTemplate,
  chaos_headerTemplate,
  google_EndpointTemplate,
  google_bodyTemplate,
  google_headerTemplate,
  google_inputPricePerMillion,
  google_outputPricePerMillion,
  mistral_EndpointTemplate,
  mistral_bodyTemplate,
  mistral_headerTemplate,
  mistral_inputPricePerMillion,
  mistral_outputPricePerMillion,
  ollama_EndpointTemplate,
  ollama_bodyTemplate,
  ollama_headerTemplate,
  ollama_inputPricePerMillion,
  ollama_outputPricePerMillion,
  openAI_EndpointTemplate,
  openAI_bodyTemplate,
  openAI_headerTemplate,
  openAI_inputPricePerMillion,
  openAI_outputPricePerMillion,
} from "../modelTemplates";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation } from "aws-amplify";
import { getLLMService } from "../graphql/queries";
import { GetLLMServiceQuery } from "../API";
import { GraphQLResult } from "@aws-amplify/api";
import { createLLMServiceAfterEdit } from "../dataaccess/createLLMServiceAfterEdit";
import { useUser } from "../contexts/useUser";
import { updateLLMServiceAfterEdit } from "../dataaccess/updateServiceAfterEdit";
import { useLLM } from "llmasaservice-client";
import { useGoogleAnalytics } from "../hooks/useGoogleAnalytics";
import { fetchLLMGroups } from "../dataaccess/fetchLLMGroups";

const schema = z.object({
  id: z.string().optional(),
  //projectId: z.string(),
  name: z.string().min(1, "Name is required"),
  status: z.string().min(1, "Status is required"),
  vendor: z.string().min(1, "Vendor is required"),
  region: z.string().min(1, "Region is required"),
  endpoint: z
    .string()
    .optional()
    .nullable()
    .refine(
      (value) => {
        if (!value) return true;
        // Check if the value is a valid URL when it's not blank
        try {
          new URL(value);
          return true; // Valid URL
        } catch {
          return false; // Invalid URL
        }
      },
      {
        message:
          "Must be a valid URL to the vendors LLM endpoint or blank if not used.",
      }
    ),
  body: z
    .string()
    .refine(
      (value) => {
        if (!value) return true;

        try {
          JSON.parse(value.replace("{{API-KEY}}", "1"));
          return true;
        } catch {
          return false;
        }
      },
      {
        message: "Body must be a valid JSON string",
      }
    )
    .nullable()
    .optional(),
  header: z
    .string()
    .refine(
      (value) => {
        if (!value) return true;

        try {
          JSON.parse(value.replace("{{API-KEY}}", "1"));
          return true;
        } catch {
          return false;
        }
      },
      {
        message: "Header must be a valid JSON string",
      }
    )
    .nullable()
    .optional(),
  loadProportion: z.number().int().min(0).max(100).nullable().optional(),
  inputTokenPricePerMillion: z.coerce.number().nullable().optional(),
  outputTokenPricePerMillion: z.coerce.number().nullable().optional(),
  groupId: z.string().optional().nullable(),
});

type ServiceFormType = z.infer<typeof schema>;

const ServiceForm = () => {
  const { id } = useParams<{ id: string }>();
  const { user, project } = useUser();
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
  const [openAPIKeyDialog, setOpenAPIKeyDialog] = React.useState(false);
  const [APIKey, setAPIKey] = useState<string>("");
  const [APIKeyError, setAPIKeyError] = useState<string>("");
  const [noAPIKeyError, setNoAPIKeyError] = useState<string>("");
  const [AWSSecretAccessKey, setAWSSecretAccessKey] = useState<string>("");
  const [AWSSecretAccessKeyError, setAWSSecretAccessKeyError] =
    useState<string>("");
  const [APIKeySuccessMessage, setAPIKeySuccessMessage] = useState<string>("");
  const [saveButDontClose, setSaveButDontClose] = useState<boolean>(false);
  const [redactedApiKeyResult, setRedactedApiKeyResult] = useState<any>(null);

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  const {
    send,
    response: streamData,
    idle,
  } = useLLM({
    project_id: project?.id ?? "",
    url: url,
  });
  const { logEvent } = useGoogleAnalytics();

  const location = useLocation();
  const {
    name,
    vendor,
    model,
    endpoint,
    header,
    body,
    inputprice,
    outputprice,
    group,
  } = location.state?.data || {};

  const navigate = useNavigate();
  const { logError } = useErrorLogging();

  useEffect(() => {
    if (!project) return;
    if (!id) return;
    getCurrentApiKey();
  }, [id]);

  const queryClient = useQueryClient();
  const mutationUpdateService = useMutation({
    mutationFn: updateLLMServiceAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["services", { id: id }] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });

      if (!saveButDontClose) {
        window.history && window.history.length > 1
          ? navigate(-1)
          : navigate("/");
      } else {
        setSaveButDontClose(false);
      }
    },
    onError: (error) => {
      logError("Error updating service", error as any);
    },
  });

  const mutationCreateService = useMutation({
    mutationFn: createLLMServiceAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });

      if (!saveButDontClose) {
        const data = {
          newService: true,
        };
        navigate("/services", { state: { data } });
      } else {
        setSaveButDontClose(false);
      }
    },
    onError: (error) => {
      logError("Error creating service", error as any);
    },
  });

  const {
    data: serviceData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["services", { id: id }],
    queryFn: () =>
      API.graphql(graphqlOperation(getLLMService, { id: id })) as Promise<
        GraphQLResult<GetLLMServiceQuery>
      >,
    enabled: !!id,
  });

  const {
    data: groupData,
    isLoading: isLoadingGroups,
    isError: isErrorGroups,
  } = useQuery({
    queryKey: ["groups"],
    queryFn: () => fetchLLMGroups(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  const initialValues = {
    name: name || "",
    status: "draft",
    vendor: vendor || "openai",
    region: "americas",
    endpoint: endpoint || openAI_EndpointTemplate,
    body: body || openAI_bodyTemplate,
    header: header || openAI_headerTemplate,
    loadProportion: 100,
    inputTokenPricePerMillion: inputprice || openAI_inputPricePerMillion,
    outputTokenPricePerMillion: outputprice || openAI_outputPricePerMillion,
    groupId: group
      ? groupData?.data?.groupsByProjectId?.items.find((g) =>
          g?.name.toLowerCase().includes(group.toLowerCase())
        )?.id ?? ""
      : "",
  };

  const { handleSubmit, control, reset, watch, setValue } =
    useForm<ServiceFormType>({
      values: serviceData?.data?.getLLMService || initialValues,
      resolver: zodResolver(schema),
    });

  if (id && isLoading && isLoadingGroups) return <div>Loading...</div>;
  if (id && isError && isErrorGroups) return <div>Error loading service</div>;

  const handleUpdate = (values: ServiceFormType) => {
    if (!values) return;

    try {
      mutationUpdateService.mutate({ id: id ?? "undefined", ...values });
    } catch (error) {
      logError("Error updating service", error as any);
    }
  };

  const handleCreate = (values: ServiceFormType) => {
    if (!values) return;
    if (!project) return;

    mutationCreateService.mutate({
      projectId: project?.id,
      ...values,
    });
  };

  const handleClose = () => {
    window.history && window.history.length > 1 ? navigate(-1) : navigate("/");
  };

  const onSubmit = (data: ServiceFormType) => {
    if (
      data.vendor !== "chaos" &&
      data.vendor !== "amazon" &&
      !data.header?.includes("{{API-KEY}}") &&
      !data.endpoint?.includes("{{API-KEY}}")
    ) {
      setNoAPIKeyError("Header OR Endpoint MUST contain an {{API-KEY}} token");
      return;
    }

    if (id) handleUpdate(data);
    else handleCreate(data);
  };

  const headerValue = watch("header");
  const bodyValue = watch("body");
  const vendorValue = watch("vendor");

  const handleVendorChange = (e: any) => {
    if (vendorValue === "") return;
    if (vendorValue === "other") return;

    if (headerValue !== "" || bodyValue !== "") {
      // ask the user if they want to overwrite the current values
      setOpenConfirmDialog(true);
    } else {
      // overwrite the current values with the template
      handleConfirmConfirmDialog();
    }
  };

  const handleConfirmConfirmDialog = () => {
    switch (vendorValue) {
      case "openai":
        setValue("endpoint", openAI_EndpointTemplate);
        setValue("header", openAI_headerTemplate);
        setValue("body", openAI_bodyTemplate);
        setValue("inputTokenPricePerMillion", openAI_inputPricePerMillion);
        setValue("outputTokenPricePerMillion", openAI_outputPricePerMillion);
        break;
      case "microsoft":
        setValue("endpoint", azure_EndpointTemplate);
        setValue("header", azure_headerTemplate);
        setValue("body", azure_bodyTemplate);
        setValue("inputTokenPricePerMillion", azure_inputPricePerMillion);
        setValue("outputTokenPricePerMillion", azure_outputPricePerMillion);
        break;
      case "chaos":
        setValue("endpoint", chaos_EndpointTemplate);
        setValue("header", chaos_headerTemplate);
        setValue("body", chaos_bodyTemplate);
        break;
      case "anthropic":
        setValue("endpoint", anthropic_EndpointTemplate);
        setValue("header", anthropic_headerTemplate);
        setValue("body", anthropic_bodyTemplate);
        setValue("inputTokenPricePerMillion", anthropic_inputPricePerMillion);
        setValue("outputTokenPricePerMillion", anthropic_outputPricePerMillion);
        break;
      case "google":
        setValue("endpoint", google_EndpointTemplate);
        setValue("header", google_headerTemplate);
        setValue("body", google_bodyTemplate);
        setValue("inputTokenPricePerMillion", google_inputPricePerMillion);
        setValue("outputTokenPricePerMillion", google_outputPricePerMillion);
        break;
      case "amazon":
        setValue("endpoint", bedrock_EndpointTemplate);
        setValue("header", bedrock_headerTemplate);
        setValue("body", bedrock_bodyTemplate);
        break;
      case "mistral":
        setValue("endpoint", mistral_EndpointTemplate);
        setValue("header", mistral_headerTemplate);
        setValue("body", mistral_bodyTemplate);
        setValue("inputTokenPricePerMillion", mistral_inputPricePerMillion);
        setValue("outputTokenPricePerMillion", mistral_outputPricePerMillion);
        break;
      case "ollama":
        setValue("endpoint", ollama_EndpointTemplate);
        setValue("header", ollama_headerTemplate);
        setValue("body", ollama_bodyTemplate);
        setValue("inputTokenPricePerMillion", ollama_inputPricePerMillion);
        setValue("outputTokenPricePerMillion", ollama_outputPricePerMillion);
        break;
      default:
        break;
    }

    setOpenConfirmDialog(false);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  function handleSaveAPIKeyDialog(event: any): void {
    if (!APIKey || APIKey.length < 1) {
      setAPIKeyError("API Key is required to save, or click cancel");
      return;
    }

    if (
      vendorValue === "amazon" &&
      (!AWSSecretAccessKey || AWSSecretAccessKey.length < 1)
    ) {
      setAWSSecretAccessKeyError(
        "AWS Secret Access Key is required to save, or click cancel"
      );
      return;
    }

    API.post("eventapi", "/updateApiKey", {
      body: {
        userId: user?.id,
        projectId: project?.id,
        serviceId: id,
        apiKey:
          vendorValue === "amazon"
            ? JSON.stringify({
                aws_access_key_id: APIKey,
                aws_secret_access_key: AWSSecretAccessKey,
              })
            : APIKey,
      },
    });

    setAPIKeySuccessMessage("API Key saved successfully");
    setOpenAPIKeyDialog(false);
  }

  const getCurrentApiKey = async () => {
    if (!id) return;
    if (!project) return;

    const result = await API.post("eventapi", "/getRedactedApiKey", {
      body: {
        projectId: project?.id,
        serviceId: id,
      },
    });

    setRedactedApiKeyResult(result);
  };

  const canUseMasterKey = () => {
    console.log(vendorValue);
    return (
      vendorValue === "openai" ||
      vendorValue === "google" ||
      vendorValue === "anthropic" ||
      vendorValue === "mistral"
    );
  };

  return (
    <Paper
      sx={{ margin: 2, padding: 2, borderRadius: 3, border: "1px solid #444" }}
    >
      <Typography padding={2} variant="h6">
        LLM Service
      </Typography>
      <Grid container padding={2} spacing={2}>
        <Grid item xs={12} md={6}>
          <TextFieldElement
            control={control}
            name="name"
            label="Name"
            size="small"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="status"
            label="Service status"
            size="small"
            required
            fullWidth
            options={[
              { id: "draft", label: "Draft" },
              { id: "active", label: "Active" },
              { id: "disabled", label: "Disabled" },
              {
                id: "errors",
                label: "Disabled (due to errors)",
              },
              { id: "deleted", label: "Deleted" },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            name="vendor"
            label="LLM vendor"
            control={control}
            required
            size="small"
            fullWidth
            onChange={handleVendorChange}
            options={[
              {
                id: "amazon",
                label: "Amazon (AWS Bedrock)",
              },
              {
                id: "anthropic",
                label: "Anthropic",
              },
              { id: "google", label: "Google (Gemini)" },
              { id: "openai", label: "OpenAI" },
              { id: "microsoft", label: "Microsoft (Azure)" },
              { id: "mistral", label: "Mistral" },
              { id: "chaos", label: "Chaos Monkey Fault Simulator" },
              { id: "ollama", label: "Ollama" },
            ]}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SelectElement
            name="groupId"
            label="Service Group"
            control={control}
            size="small"
            fullWidth
            options={[
              { id: "", label: "Default" },
              ...(groupData?.data?.groupsByProjectId?.items?.map((group) => ({
                id: group?.id ?? "",
                label: group?.name ?? "",
              })) ?? []),
            ]}
          />
        </Grid>
      </Grid>
      <hr />
      <Grid container padding={2} spacing={2}>
        <Grid item xs={12}>
          <Typography variant="caption">
            use the symbol: &#123;&#123;API-KEY&#125;&#125; to insert your
            securely stored key in the Endpoint or Header.
          </Typography>
          {noAPIKeyError !== "" && (
            <>
              <br />
              <Typography variant="caption" color="error">
                {noAPIKeyError}
              </Typography>
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <TextFieldElement
            control={control}
            name="endpoint"
            label="Endpoint URL"
            size="small"
            fullWidth
            //disabled={vendorValue === "chaos" || vendorValue === "amazon"}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextareaAutosizeElement
            control={control}
            name="header"
            label="Request Header"
            size="small"
            fullWidth
            rows={8}
            //disabled={vendorValue === "chaos" || vendorValue === "amazon"}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextareaAutosizeElement
            control={control}
            name="body"
            label="Request Body"
            size="small"
            fullWidth
            rows={8}
          />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ spacing: 2 }} display="flex" alignItems="center">
            <Button
              variant="outlined"
              color="primary"
              sx={{ marginRight: 2 }}
              onClick={() => setOpenAPIKeyDialog(true)}
              disabled={!id}
            >
              Add or update API Key
            </Button>
            <Box display="flex" alignItems="center">
              <Button
                variant="outlined"
                color="primary"
                disabled={!id || !idle}
                onClick={() => {
                  send("What is 1 + 1?", [], [], true, false, id);
                  logEvent("Service", "Test Call Clicked");
                }}
              >
                Make a test call
              </Button>

              {streamData && (
                <>
                  <br />
                  <Typography variant="caption">
                    &nbsp;&nbsp;Response: {streamData}{" "}
                    {streamData.includes("2") ? (
                      <CheckCircleIcon style={{ color: "green" }} />
                    ) : (
                      <CancelIcon style={{ color: "red" }} />
                    )}
                  </Typography>
                </>
              )}
            </Box>

            {!id && (
              <>
                <br />{" "}
                <Typography variant="caption">
                  Save the service to enable these options
                </Typography>
              </>
            )}
          </Box>
          <Box>
            <Typography variant="caption">{APIKeySuccessMessage}</Typography>{" "}
            <Typography variant="caption">
              {redactedApiKeyResult
                ? redactedApiKeyResult.keyFound
                  ? redactedApiKeyResult.redactedApiKey
                  : `No user key added. ${canUseMasterKey() ? "API credits WILL be use for this vendor." : "You must add a key to use this vendor."}`
                : ""}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <hr />
      <Grid container padding={2} spacing={2}>
        <Grid item xs={12}>
          <Typography variant="caption">
            These values are used to calculate the cost of the service. The
            values are per million tokens. These change frequently and depend on
            the model you use, please check the vendor's pricing page for the
            most up-to-date values.
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextFieldElement
            control={control}
            name="inputTokenPricePerMillion"
            label="Input Token Price Per Million"
            size="small"
            fullWidth
            type="currency"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextFieldElement
            control={control}
            name="outputTokenPricePerMillion"
            label="Output Token Price Per Million"
            size="small"
            fullWidth
            type="currency"
          />
        </Grid>
      </Grid>
      <hr />
      <Box sx={{ padding: 2 }}>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant={"contained"}
          sx={{ marginRight: 2 }}
        >
          Save and Close
        </Button>
        <Button
          onClick={() => {
            setSaveButDontClose(true);
            handleSubmit(onSubmit)();
          }}
          variant={"outlined"}
        >
          Save
        </Button>
        <Button onClick={() => reset()} variant={"outlined"}>
          Reset
        </Button>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
      </Box>

      <Dialog open={openConfirmDialog} onClose={handleCloseConfirmDialog}>
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          Do you want to replace the current body and header with a template for
          this vendor?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
          <Button onClick={handleConfirmConfirmDialog} color="primary">
            Replace
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAPIKeyDialog}
        onClose={() => setOpenAPIKeyDialog(false)}
      >
        <DialogTitle>API Key</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Safely storing your API Key is handled by us. Once added it cannot
            be viewed or retrieved, but you can (and should) update it
            regularly. Each vendor requires an API Key to access their services,
            and you can see instructions for each vendor in our help docs.
          </Typography>
          <br />

          <TextField
            sx={{ marginBottom: 2 }}
            label={vendorValue === "amazon" ? "AWS Access Key Id" : "API Key"}
            size="small"
            required
            fullWidth
            onChange={(e) => setAPIKey(e.target.value)}
            error={APIKeyError !== ""}
            helperText={APIKeyError}
          />

          {vendorValue === "amazon" && (
            <TextField
              label="AWS Secret Access Key"
              size="small"
              required
              fullWidth
              onChange={(e) => setAWSSecretAccessKey(e.target.value)}
              error={AWSSecretAccessKeyError !== ""}
              helperText={AWSSecretAccessKeyError}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAPIKeyDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveAPIKeyDialog} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ServiceForm;

/*
 <SelectElement
          control={control}
          name="vendor"
          label="Vendor"
          size="small"
          required
          sx={{ minWidth: 50 }}
      
          options={[
            { key:"none", value: "", label: "Select a vendor" },
            { value: "openai", label: "OpenAI" },
            { value: "amazon", label: "Amazon (AWS)" },
            { value: "microsoft", label: "Microsoft (Azure)" },
            { value: "google", label: "Google (GCP)" },
          ]}
        /> */
